import { AxDataTable } from "@/components/ax/dataTable/AxDataTable";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { ROUTES } from "@/constants/routeConstants";
import { AreaType } from "@/lib/types";
import { deleteAreaType, getAreaType } from "@/sideEffects/api";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { showSuccess, showApiError } from "@/lib/toast";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { PermissionChecker2 } from "@/components/permission/PermissionChecker2";
import { motion } from "framer-motion";
import { AddRowButton } from "@/components/ax/dataTable/AddRowButton";
import { PageLayout } from "@/components/PageLayout";
import { ViewRowButton } from "@/components/ax/dataTable/ViewRowButton";

export const AreaTypePage = () => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  useBreadcrumbs([
    {
      name: t("entities.areaType"),
      path: ROUTES.AREA_TYPE,
    },
  ]);

  const { goToAreaTypeDetail, goToAreaTypeAdd } = useAppNavigate();

  const columns: ColumnDef<AreaType>[] = [
    {
      accessorKey: "name",
      header: t("common.name"),
      cell: ({ row }) => <div className="font-medium text-primary">{row.original.name}</div>,
    },
    {
      accessorKey: "color",
      header: t("common.color"),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2">
            <motion.div
              whileHover={{ scale: 1.2 }}
              className="rounded-md w-[30px] h-[30px] border shadow-sm"
              style={{
                backgroundColor: row.original.color,
              }}
            />
            <span className="text-sm text-muted-foreground">{row.original.color}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "id",
      header: () => "",
      cell: ({ row }) => {
        return (
          <PermissionChecker2 entity="AreaType" rights={["GetDetails"]}>
            <div className="flex items-center justify-end gap-2">
              <ViewRowButton onClickView={() => goToAreaTypeDetail(row.original.id)} />
            </div>
          </PermissionChecker2>
        );
      },
    },
  ];

  const queryClient = useQueryClient();
  const deleteAreaTypesMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => deleteAreaType(id, String(activeFirm?.firmGuid))));
    },
    onSuccess: () => {
      showSuccess(t("common.deletedSuccess"));
      queryClient.invalidateQueries({ queryKey: ["getAreaType"] });
    },
    onError: showApiError,
  });

  return (
    <PageLayout
      title={<>{t("entities.areaType")}</>}
      description={t("areaType.areaTypeDesc")}
      extraRight={
        <PermissionChecker2 entity="AreaType" rights={["Add"]}>
          <AddRowButton
            onClickAdd={goToAreaTypeAdd}
            title={t("common.addNewEntity", { entity: t("entities.areaType") })}
          />
        </PermissionChecker2>
      }
    >
      <AxDataTable
        columns={columns}
        fetcher={getAreaType}
        getRowId={(row) => row.id.toString()}
        uniqQueryKey="getAreaType"
        dynamicRequestParams={{
          Firm_Id: String(activeFirm?.firmGuid),
        }}
        hidePageSizeSelector
        numberOfObjectsPerPage={999999}
        onDeleteRows={deleteAreaTypesMutation.mutateAsync}
        deleting={deleteAreaTypesMutation.isPending}
      />
    </PageLayout>
  );
};
